// Tradução pt-BR da interface do Cloudflare RealtimeKit (RtkMeeting).
// O SDK vem em inglês por padrão; este dicionário é mesclado sobre o idioma
// padrão via `useLanguage(...)` e passado ao componente pela prop `t`.
// Mantido como Partial<LangDict> para tolerar diferenças de chaves entre versões
// do SDK (chaves não traduzidas continuam no inglês padrão, sem quebrar a build).

import type { LangDict } from "@cloudflare/realtimekit-react-ui";

export const rtkLanguagePtBr: Partial<LangDict> = {
    about_call: "Sobre a chamada",
    screen: "Tela",
    camera: "Câmera",
    leave: "Sair",
    dismiss: "Dispensar",
    logo: "Logo",
    page: "Página",
    more: "Mais",
    "page.prev": "Página anterior",
    "page.next": "Próxima página",
    layout: "Layout",
    "layout.auto": "Layout automático",
    settings: "Configurações",
    file: "Arquivo",
    image: "Imagem",
    connection: "Conexão",
    leave_confirmation: "Tem certeza de que deseja sair da chamada?",
    cancel: "Cancelar",
    yes: "Sim",
    "(you)": "(você)",
    you: "Você",
    everyone: "Todos",
    to: "Para",
    mute: "Silenciar",
    kick: "Remover",
    pin: "Fixar",
    pinned: "Fixado",
    accept: "Aceitar",
    unpin: "Desafixar",
    pip_on: "Mostrar miniatura flutuante",
    pip_off: "Ocultar miniatura flutuante",
    viewers: "Espectadores",
    join: "Entrar",
    joined: "Entrou",
    create: "Criar",
    close: "Fechar",
    ask: "Perguntar",
    type: "Digitar",
    activate: "Ativar",
    requests: "Solicitações",
    mic_off: "Microfone desligado",
    disable_mic: "Desligar microfone",
    mic_on: "Microfone ligado",
    enable_mic: "Ligar microfone",
    audio: "Áudio",
    test: "Testar",
    minimize: "Ocultar miniatura",
    maximize: "Mostrar miniatura",
    mute_all: "Silenciar todos",
    "mute_all.description": "Todos os outros participantes da reunião serão silenciados.",
    "mute_all.header": "Tem certeza?",
    "mute_all.allow_unmute": "Permitir que outros ativem o microfone",
    video_off: "Câmera desligada",
    disable_video: "Desligar câmera",
    video_on: "Câmera ligada",
    enable_video: "Ligar câmera",
    video: "Vídeo",
    offline: "Você está offline",
    "offline.description": "Verifique se você está conectado à internet.",
    disconnected: "Você não entrou na reunião.",
    failed: "Você foi desconectado",
    "failed.description": "Não conseguimos reconectar você à sala. Tente entrar na reunião novamente.",
    "disconnected.description": "Entre na reunião para ver e interagir com os outros participantes.",
    participants: "Participantes",
    "participants.errors.empty_results": "Não encontramos um participante com o nome ou ID informado.",
    "participants.empty_list": "Parece que não há ninguém aqui.",
    "participants.no_pending_requests": "Não há solicitações pendentes.",
    "participants.turn_off_video": "Desligar vídeo",
    polls: "Enquetes",
    "polls.by": "Enquete de",
    "polls.question": "Pergunta da enquete",
    "polls.question.placeholder": "Sobre o que é a sua enquete?",
    "polls.answers": "Respostas",
    "polls.option": "Adicionar uma opção.",
    "polls.option.placeholder": "Digite uma opção",
    "polls.results.anon": "Anônimo",
    "polls.results.hide": "Ocultar resultados antes de votar",
    "polls.create": "Criar enquete",
    "polls.cancel": "Cancelar criação da enquete",
    "polls.empty": "Nenhuma enquete disponível",
    "polls.errors.question_required": "A pergunta é obrigatória.",
    "polls.errors.empty_option": "Não são permitidas opções vazias.",
    screenshare: "Compartilhamento de tela",
    "screenshare.min_preview": "Minimizar prévia",
    "screenshare.max_preview": "Expandir prévia",
    "screenshare.shared": "Sua tela está sendo compartilhada.",
    "screenshare.start": "Compartilhar tela",
    "screenshare.stop": "Parar compartilhamento",
    "screenshare.error.unknown": "Ocorreu um erro ao iniciar o compartilhamento de tela.",
    "screenshare.error.max_count": "Limite máximo de compartilhamentos de tela atingido.",
    plugins: "Plugins",
    perm_denied: "Permissão negada pelo navegador.",
    "perm_denied.audio": "Permissão de microfone negada pelo navegador",
    "perm_denied.video": "Permissão de câmera negada pelo navegador",
    "perm_denied.screenshare": "Permissão de compartilhamento de tela negada pelo navegador",
    "perm_denied.audio.chrome.message": "Na barra de endereço, clique no ícone à esquerda da URL e verifique se a permissão de 'Microfone' está marcada.",
    "perm_denied.video.chrome.message": "Na barra de endereço, clique no ícone à esquerda da URL e verifique se a permissão de 'Câmera' está marcada.",
    "perm_denied.screenshare.chrome.message": "Nas configurações do Chrome, vá em 'Privacidade e segurança > Configurações do site > Permissões'. Selecione 'Compartilhamento de tela', conceda a permissão e recarregue este aplicativo.",
    "perm_denied.audio.safari.message": "Nas configurações do Safari, vá em 'Sites > Microfone', conceda a permissão e recarregue este aplicativo.",
    "perm_denied.video.safari.message": "Nas configurações do Safari, vá em 'Sites > Câmera', conceda a permissão e recarregue este aplicativo.",
    "perm_denied.screenshare.safari.message": "Nas configurações do Safari, vá em 'Sites > Compartilhamento de tela', conceda a permissão e recarregue este aplicativo.",
    "perm_denied.audio.edge.message": "Nas configurações do Edge, vá em 'Permissões do site > Microfone', conceda a permissão e recarregue este aplicativo.",
    "perm_denied.video.edge.message": "Nas configurações do Edge, vá em 'Permissões do site > Câmera', conceda a permissão e recarregue este aplicativo.",
    "perm_denied.screenshare.edge.message": "Nas configurações do Edge, vá em 'Permissões do site > Compartilhamento de tela', conceda a permissão e recarregue este aplicativo.",
    "perm_denied.audio.microsoft edge.message": "Nas configurações do Edge, vá em 'Permissões do site > Microfone', conceda a permissão e recarregue este aplicativo.",
    "perm_denied.video.microsoft edge.message": "Nas configurações do Edge, vá em 'Permissões do site > Câmera', conceda a permissão e recarregue este aplicativo.",
    "perm_denied.screenshare.microsoft edge.message": "Nas configurações do Edge, vá em 'Permissões do site > Compartilhamento de tela', conceda a permissão e recarregue este aplicativo.",
    "perm_denied.audio.firefox.message": "Nas configurações do Firefox, vá em 'Privacidade e segurança > Permissões > Microfone', conceda a permissão e recarregue este aplicativo.",
    "perm_denied.video.firefox.message": "Nas configurações do Firefox, vá em 'Privacidade e segurança > Permissões > Câmera', conceda a permissão e recarregue este aplicativo.",
    "perm_denied.screenshare.firefox.message": "Nas configurações do Firefox, vá em 'Privacidade e segurança > Permissões > Compartilhamento de tela', conceda a permissão e recarregue este aplicativo.",
    "perm_denied.audio.others.message": "Nas configurações do seu navegador, ative as permissões de 'Microfone' e recarregue este aplicativo.",
    "perm_denied.video.others.message": "Nas configurações do seu navegador, ative as permissões de 'Câmera' e recarregue este aplicativo.",
    "perm_denied.screenshare.others.message": "Nas configurações do seu navegador, ative as permissões de 'Compartilhamento de tela' e recarregue este aplicativo.",
    perm_sys_denied: "Permissão negada pelo sistema",
    "perm_sys_denied.audio": "Permissão de microfone negada pelo sistema",
    "perm_sys_denied.video": "Permissão de câmera negada pelo sistema",
    "perm_sys_denied.screenshare": "Permissão de compartilhamento de tela negada pelo sistema",
    "perm_sys_denied.audio.macos.message": "Abra o menu Apple e vá em 'Ajustes do Sistema > Privacidade e Segurança > Microfone'. Permita o acesso ao seu navegador e recarregue este aplicativo.",
    "perm_sys_denied.video.macos.message": "Abra o menu Apple e vá em 'Ajustes do Sistema > Privacidade e Segurança > Câmera'. Permita o acesso ao seu navegador e recarregue este aplicativo.",
    "perm_sys_denied.screenshare.macos.message": "Abra o menu Apple e vá em 'Ajustes do Sistema > Privacidade e Segurança > Gravação de tela'. Permita o acesso ao seu navegador e recarregue este aplicativo.",
    "perm_sys_denied.audio.ios.message": "No seu iPhone, vá em 'Ajustes > Privacidade > Microfone', permita o acesso ao seu navegador e recarregue este aplicativo.",
    "perm_sys_denied.video.ios.message": "No seu iPhone, vá em 'Ajustes > Privacidade > Câmera', permita o acesso ao seu navegador e recarregue este aplicativo.",
    "perm_sys_denied.screenshare.ios.message": "No seu iPhone, vá em 'Ajustes > Privacidade > Gravação de tela', permita o acesso ao seu navegador e recarregue este aplicativo.",
    "perm_sys_denied.audio.windows.message": "Nas configurações do Windows, vá em 'Configurações > Privacidade > Microfone'. Permita o acesso ao seu navegador e recarregue este aplicativo.",
    "perm_sys_denied.video.windows.message": "Nas configurações do Windows, vá em 'Configurações > Privacidade > Câmera'. Permita o acesso ao seu navegador e recarregue este aplicativo.",
    "perm_sys_denied.screenshare.windows.message": "Nas configurações do Windows, vá em 'Configurações > Privacidade > Compartilhamento de tela'. Permita o acesso ao seu navegador e recarregue este aplicativo.",
    "perm_sys_denied.audio.android.message": "No seu dispositivo, vá em 'Configurações > Apps'. Selecione seu navegador, permita o acesso ao Microfone e recarregue este aplicativo.",
    "perm_sys_denied.video.android.message": "No seu dispositivo, vá em 'Configurações > Apps'. Selecione seu navegador, permita o acesso à Câmera e recarregue este aplicativo.",
    "perm_sys_denied.screenshare.android.message": "No seu dispositivo, vá em 'Configurações > Apps'. Selecione seu navegador, permita o compartilhamento de tela e recarregue este aplicativo.",
    "perm_sys_denied.audio.others.message": "Acesse as configurações do seu sistema. Permita o acesso ao 'Microfone' para o seu navegador e recarregue este aplicativo.",
    "perm_sys_denied.video.others.message": "Acesse as configurações do seu sistema. Permita o acesso à 'Câmera' para o seu navegador e recarregue este aplicativo.",
    "perm_sys_denied.screenshare.others.message": "Acesse as configurações do seu sistema. Permita o 'Compartilhamento de tela' para o seu navegador e recarregue este aplicativo.",
    perm_could_not_start: "Não foi possível acessar o dispositivo.",
    "perm_could_not_start.audio": "Não foi possível iniciar o seu microfone",
    "perm_could_not_start.video": "Não foi possível iniciar a sua câmera",
    "perm_could_not_start.screenshare": "Não foi possível iniciar o seu compartilhamento de tela",
    "perm_could_not_start.audio.message": "Parece que o sistema não conseguiu acessar o seu microfone. Reinicie o dispositivo ou atualize o navegador para resolver.",
    "perm_could_not_start.video.message": "Parece que o sistema não conseguiu acessar a sua câmera. Reinicie o dispositivo ou atualize o navegador para resolver.",
    "perm_could_not_start.screenshare.message": "Parece que o sistema não conseguiu capturar o seu compartilhamento de tela. Reinicie o dispositivo ou atualize o navegador para resolver.",
    full_screen: "Tela cheia",
    "full_screen.exit": "Sair da tela cheia",
    "waitlist.header_title": "Aguardando",
    "waitlist.body_text": "Você está na sala de espera. O anfitrião vai liberar sua entrada em breve.",
    "waitlist.deny_request": "Recusar solicitação",
    "waitlist.accept_request": "Aceitar solicitação",
    "waitlist.accept_all": "Aceitar todos",
    "stage_request.header_title": "Solicitações para entrar no palco",
    "stage_request.deny_request": "Recusar solicitação",
    "stage_request.accept_request": "Aceitar solicitação",
    "stage_request.accept_all": "Aceitar todos",
    "stage_request.deny_all": "Recusar todos",
    "stage_request.approval_pending": "Pendente",
    "stage_request.denied": "Recusado",
    "stage_request.request": "Entrar no palco",
    "stage_request.requested": "Solicitado",
    "stage_request.cancel_request": "Cancelar solicitação",
    "stage_request.leave_stage": "Sair do palco",
    "stage_request.request_tip": "Solicitar para participar da conversa",
    "stage_request.leave_tip": "Sair do palco",
    "stage_request.pending_tip": "Solicitação pendente",
    "stage_request.denied_tip": "Solicitação recusada",
    "stage.empty_host": "O palco está vazio",
    "stage.empty_host_summary": "Você está fora do palco. Você pode gerenciar as solicitações na aba de participantes.",
    "stage.empty_viewer": "Não há ninguém no palco",
    "stage.remove_from_stage": "Remover do palco",
    "stage.invited_notification": "foi convidado(a) a entrar no palco.",
    "stage.add_to_stage": "Convidar para o palco",
    "stage.join_title": "Entrar no palco",
    "stage.join_summary": "Você está prestes a entrar no palco. Seu vídeo e áudio, como exibidos acima, ficarão visíveis para todos os participantes.",
    "stage.join_cancel": "Cancelar",
    "stage.join_confirm": "Entrar",
    "setup_screen.join_in_as": "Entrando como",
    "setup_screen.your_name": "Seu nome",
    "stage.reconnecting": "Reconectando...",
    "recording.label": "GRAV",
    "recording.indicator": "Esta reunião está sendo gravada.",
    "recording.started": "Esta reunião está sendo gravada.",
    "recording.stopped": "A gravação desta reunião foi interrompida.",
    "recording.paused": "A gravação desta reunião foi pausada.",
    "recording.error.start": "Erro ao iniciar a gravação.",
    "recording.error.stop": "Erro ao interromper a gravação.",
    "recording.error.resume": "Erro ao retomar a gravação.",
    "recording.start": "Iniciar gravação",
    "recording.stop": "Parar gravação",
    "recording.resume": "Retomar gravação",
    "recording.starting": "Iniciando",
    "recording.stopping": "Interrompendo",
    "recording.loading": "Carregando",
    "recording.idle": "Gravar",
    audio_playback: "Reproduzir áudio",
    "audio_playback.title": "Permitir reprodução de áudio",
    "audio_playback.description": "Para reproduzir o áudio corretamente no seu dispositivo, toque no botão abaixo.",
    breakout_rooms: "Salas paralelas",
    "breakout_rooms.room_config_header": "Criar sala paralela",
    "breakout_rooms.join_breakout_header": "Entrar na sala paralela",
    "breakout_rooms.empty": "Ainda não há ninguém aqui.",
    "breakout_rooms.delete": "Excluir sala",
    "breakout_rooms.switch": "Trocar",
    "breakout_rooms.main_room": "Sala principal",
    "breakout_rooms.shuffle_participants": "Embaralhar participantes",
    "breakout_rooms.deselect": "Desmarcar",
    "breakout_rooms.selected": "selecionado(s)",
    "breakout_rooms.num_of_rooms": "Nº de salas",
    "breakout_rooms.approx": "Aprox.",
    "breakout_rooms.participants_per_room": "participantes/sala",
    "breakout_rooms.division_text": "quando divididos igualmente.",
    "breakout_rooms.start_breakout": "Iniciar salas paralelas",
    "breakout_rooms.close_breakout": "Encerrar salas paralelas",
    "breakout_rooms.update_breakout": "Atualizar salas paralelas",
    "breakout_rooms.discard_changes": "Descartar alterações",
    "breakout_rooms.room": "Sala",
    "breakout_rooms.rooms": "Salas",
    "breakout_rooms.room_name": "Nome da sala",
    "breakout_rooms.edit_room_name": "Editar nome da sala",
    "breakout_rooms.save_room_name": "Salvar nome da sala",
    "breakout_rooms.add_room": "Adicionar",
    "breakout_rooms.add_room_brief": "Adicionar sala",
    "breakout_rooms.select_all": "Selecionar todos",
    "breakout_rooms.unassign_all": "Remover todas as atribuições",
    "breakout_rooms.assign": "Atribuir",
    "breakout_rooms.assign_participants": "Atribuir participantes",
    "breakout_rooms.none_assigned": "Nenhum participante atribuído ainda",
    "breakout_rooms.drag_drop_participants": "Arraste e solte os participantes",
    "breakout_rooms.click_drop_participants": "Clique aqui para atribuir",
    "breakout_rooms.status.assign_multiple": "Atribua vários participantes de uma vez clicando e selecionando-os",
    "breakout_rooms.status.select_room": "Selecione uma sala para atribuir",
    "breakout_rooms.ephemeral_status.participants_assigned": "Participantes atribuídos",
    "breakout_rooms.ephemeral_status.participants_assigned_randomly": "Participantes atribuídos aleatoriamente",
    "breakout_rooms.ephemeral_status.changes_discarded": "Alterações descartadas",
    "breakout_rooms.confirm_modal.start_breakout.header": "Iniciar salas paralelas?",
    "breakout_rooms.confirm_modal.start_breakout.content": "Ao iniciar, todos os participantes serão movidos para as salas atribuídas.",
    "breakout_rooms.confirm_modal.start_breakout.cancelText": "Não, voltar",
    "breakout_rooms.confirm_modal.start_breakout.ctaText": "Sim, iniciar",
    "breakout_rooms.confirm_modal.close_breakout.header": "Encerrar salas paralelas?",
    "breakout_rooms.confirm_modal.close_breakout.content": "Todas as salas paralelas serão encerradas e os participantes voltarão para a sala principal.",
    "breakout_rooms.confirm_modal.close_breakout.ctaText": "Sim, encerrar",
    "breakout_rooms.move_reason.started_msg": "Iniciando salas paralelas...",
    "breakout_rooms.move_reason.started_desc": "Você está sendo movido para a sua sala",
    "breakout_rooms.move_reason.closed_msg": "Encerrando salas paralelas...",
    "breakout_rooms.move_reason.closed_desc": "Você está voltando para a sala principal",
    "breakout_rooms.move_reason.switch_room": "Entrando na sala paralela...",
    "breakout_rooms.move_reason.switch_main_room": "Entrando na sala principal...",
    "breakout_rooms.all_assigned": "Todos os participantes foram atribuídos",
    "breakout_rooms.empty_main_room": "Não há mais participantes na sala principal.",
    "breakout_rooms.leave_confirmation": "Tem certeza de que deseja sair da chamada? Você está em uma sala paralela e também pode entrar na sala principal.",
    "breakout_rooms.leave_confirmation.main_room_btn": "Voltar para a sala principal",
    ai: "IA",
    "ai.meeting_ai": "IA da reunião",
    "ai.home": "Início",
    "ai.transcriptions": "Transcrições",
    "ai.personal": "Pessoal",
    "ai.caption_view": "Visualização de legendas",
    "ai.chat.tooltip": "Esta conversa ficará visível apenas para você, e não para os outros na chamada.",
    "ai.chat.summarize": "Olá IA, resuma esta chamada",
    "ai.chat.agenda": "Olá IA, qual é a pauta de hoje?",
    search: "Buscar",
    "search.could_not_find": "Não encontramos um participante com o nome ou ID informado.",
    "search.empty": "Parece que não há ninguém aqui.",
    end: "Encerrar reunião",
    "end.all": "Encerrar reunião para todos",
    ended: "A reunião foi encerrada.",
    "ended.rejected": "Sua solicitação para entrar na reunião foi recusada.",
    "ended.left": "Você saiu da reunião.",
    "ended.kicked": "Você foi removido(a) da reunião.",
    "ended.disconnected": "A chamada foi encerrada porque a conexão foi perdida.",
    "ended.network": "Verifique sua conexão com a internet e tente novamente.",
    "ended.unauthorized": "Você não tem autorização para entrar nesta reunião.",
    network: "Rede",
    "network.reconnecting": "Conexão perdida. Tentando reconectar.",
    "network.delay_extended": "A reconexão está demorando muito. Verifique sua conexão de rede.",
    "network.disconnected": "Não foi possível reconectar. Saia da reunião e tente atualizar a janela.",
    "network.leaving": "Saindo da reunião automaticamente em 10 segundos.",
    "network.restored": "Conexão restabelecida",
    "network.delay": "A reconexão está demorando muito.",
    "network.lost": "Conexão perdida",
    "network.lost_extended": "Conexão perdida. Verifique sua conexão de rede.",
    "network.troubleshoot": "Resolver problemas de conexão",
    "join.network_error": "Não foi possível conectar à reunião. Verifique sua conexão com a internet e tente novamente.",
    "join.media_error": "Estamos com dificuldade para conectar à reunião. Tente novamente e, se o problema continuar, use uma rede diferente.",
    "join.media_firewall_error": "Sua rede pode estar bloqueando esta conexão. Use uma rede diferente ou fale com o administrador da sua rede.",
    "join.default_error": "Algo deu errado ao entrar na reunião. Tente novamente.",
    "join.error_code": "Código do erro",
    livestream: "Transmissão ao vivo",
    "livestream.indicator": "Esta reunião está sendo transmitida ao vivo.",
    "livestream.skip": "Ir para o ao vivo",
    "livestream.idle": "Aguardando para iniciar a transmissão.",
    "livestream.starting": "A transmissão está começando...",
    "livestream.stopping": "A transmissão está sendo encerrada...",
    "livestream.waiting_on_manual_ingestion": "Envie a mídia da transmissão ao vivo.",
    "livestream.error.not_supported": "Player não suportado.",
    "livestream.error.not_found": "URL de reprodução não encontrada.",
    "livestream.error.unknown": "Ocorreu um erro desconhecido.",
    "livestream.error.sync": "Não foi possível sincronizar a transmissão. Tente novamente mais tarde.",
    "livestream.error.start": "Erro ao iniciar a transmissão.",
    "livestream.error.stop": "Erro ao encerrar a transmissão.",
    "livestream.go_live": "Iniciar ao vivo",
    "livestream.end_live": "Encerrar ao vivo",
    "livestream.error": "Erro",
    "cta.help": "Precisa de ajuda para fazer isso?",
    "cta.continue": "Ignorar",
    "cta.reload": "Recarregar",
    "cta.confirmation": "Tem certeza?",
    "cta.system_settings": "Abrir configurações",
    "remote_access.empty": "Ainda não há solicitações de controle remoto.",
    "remote_access.requests": "As pessoas a seguir solicitaram controle remoto do seu compartilhamento de tela.",
    "remote_access.allow": "Selecione a quem deseja conceder acesso.",
    "remote_access.grant": "Conceder acesso",
    "remote_access.indicator": "Qualquer plugin ou compartilhamento de tela que você abrir será sincronizado com a reunião",
    chat: "Chat",
    "chat.new": "Nova",
    "chat.max_limit_warning": "Limite máximo de caracteres",
    "chat.rate_limit_error": "Aguarde antes de enviar outra mensagem",
    "chat.member_name": "Digite o nome do participante",
    "chat.add_members": "Adicionar participantes",
    "chat.delete_msg": "Excluir",
    "chat.edit_msg": "Editar",
    "chat.send_msg": "Enviar mensagem",
    "chat.send_attachment": "Solte arquivos/imagens para enviar",
    "chat.send_img": "Enviar uma imagem",
    "chat.send_file": "Enviar um arquivo",
    "chat.send_emoji": "Enviar um emoji",
    "chat.update_msg": "Atualizar mensagem",
    "chat.img.loading": "Carregando imagem",
    "chat.error.img_not_found": "Imagem não encontrada",
    "chat.error.empty_results": "Não encontramos um participante com o nome informado.",
    "chat.img.shared_by": "Compartilhado por",
    "chat.reply": "Responder",
    "chat.message_placeholder": "Mensagem...",
    "chat.click_to_send": "Clique para enviar como mensagem",
    "chat.search_msgs": "Buscar mensagens",
    "chat.search_conversations": "Buscar conversas",
    "chat.start_conversation": "Iniciar uma conversa...",
    "chat.empty_search": "Nenhuma mensagem encontrada",
    "chat.empty_chat": "Envie uma mensagem para começar",
    "chat.cancel_upload": "Cancelar envio",
    "chat.view_chats": "Ver conversas",
    "chat.everyone": "Todos",
    "chat.pinned_msgs": "Mensagens fixadas",
    "chat.toggle_pinned_msgs": "Alternar mensagens fixadas",
    "date.today": "Hoje",
    "date.yesterday": "Ontem",
    "date.sunday": "Domingo",
    "date.monday": "Segunda-feira",
    "date.tuesday": "Terça-feira",
    "date.wednesday": "Quarta-feira",
    "date.thursday": "Quinta-feira",
    "date.friday": "Sexta-feira",
    "date.saturday": "Sábado",
    "list.empty": "Nenhum item encontrado",
    "grid.listening": "Ouvindo",
    "transcript.off": "Desligar transcrições",
    "transcript.on": "Ligar transcrições",
    "settings.notification_sound": "Som de notificação",
    "settings.microphone_input": "Microfone",
    "settings.speaker_output": "Alto-falante",
    "settings.mirror_video": "Espelhar meu vídeo",
    "settings.camera_off": "A câmera está desligada",
    "dialog.close": "Fechar caixa de diálogo",
    "notifications.joined": "acabou de entrar",
    "notifications.left": "saiu",
    "notifications.requesting_to_join_meeting": "está solicitando para entrar na reunião",
    "notifications.requested_to_join_stage": "solicitou para entrar no palco",
    "notifications.joined_stage": "entrou no palco",
    "notifications.request_to_join_accepted": "Solicitação de entrada aceita",
    "notifications.request_to_join_rejected": "Solicitação de entrada recusada",
    "notifications.accept": "Aceitar",
    "notifications.new_poll_created_by": "Nova enquete criada por",
    "notifications.connected_to": "Conectado a",
    "notifications.plugin_switched_to": "Plugin alterado para",
    "notifications.remote_control_requested": "solicitou controle remoto",
    "notifications.remote_control_request_sent": "Solicitação de controle remoto enviada para",
    "notifications.remote_control_request_accepted": "concedeu o controle remoto",
    "notifications.remote_control_granted": "Controle remoto concedido a",
    "notifications.remote_control_terminated": "O controle remoto existente foi encerrado",
    "debugger.troubleshooting.label": "Solução de problemas",
    "debugger.quality.good": "Boa",
    "debugger.quality.average": "Média",
    "debugger.quality.poor": "Ruim",
    "debugger.stats.bitrate.label": "Taxa de bits",
    "debugger.stats.bitrate.description": "Dados transmitidos por segundo; afeta a qualidade e o tamanho do arquivo.",
    "debugger.stats.packet_loss.label": "Perda de pacotes",
    "debugger.stats.packet_loss.description": "Quantidade de dados perdidos durante a transferência",
    "debugger.stats.jitter.label": "Jitter",
    "debugger.stats.jitter.description": "Variação ou flutuação na latência",
    "debugger.stats.cpu_limitations.label": "Limitações de CPU",
    "debugger.stats.cpu_limitations.description": "Limitações de CPU podem afetar a qualidade e o desempenho da chamada WebRTC.",
    "debugger.stats.bandwidth_limitations.label": "Limitações de banda",
    "debugger.stats.bandwidth_limitations.description": "Conexões lentas podem reduzir a qualidade do vídeo.",
    "debugger.audio.label": "Áudio",
    "debugger.audio.troubleshooting.label": "Solução de problemas de áudio",
    "debugger.audio.messages.generating_report": "Gerando relatório. Aguarde alguns segundos.",
    "debugger.audio.messages.enable_media": "Ative o microfone para ver o relatório.",
    "debugger.audio.sections.network_media": "Rede e mídia",
    "debugger.video.label": "Vídeo",
    "debugger.video.troubleshooting.label": "Solução de problemas de vídeo",
    "debugger.video.messages.generating_report": "Gerando relatório. Aguarde alguns segundos.",
    "debugger.video.messages.enable_media": "Ative a câmera para ver o relatório.",
    "debugger.video.sections.network_media": "Rede e mídia",
    "debugger.screenshare.label": "Compartilhamento de tela",
    "debugger.screenshare.troubleshooting.label": "Solução de problemas do compartilhamento de tela",
    "debugger.screenshare.sections.network_media": "Rede e mídia",
    "debugger.screenshare.messages.generating_report": "Gerando relatório. Aguarde alguns segundos.",
    "debugger.screenshare.messages.enable_media": "Compartilhe a tela para ver o relatório.",
    "debugger.system.label": "Sistema",
    "debugger.system.troubleshooting.label": "Solução de problemas do sistema",
    "debugger.system.sections.battery": "Bateria",
    "debugger.system.battery.level.label": "Nível da bateria",
    "debugger.system.battery.level.description": "Bateria baixa pode resultar em desempenho reduzido.",
    "debugger.system.battery.charging.label": "Status de carregamento da bateria",
    "debugger.system.battery.charging.description": "Um dispositivo ligado à energia tem desempenho ideal.",
    "debugger.system.battery.charging.is_charging": "Carregando",
    "debugger.system.battery.charging.is_not_charging": "Não está carregando",
};
